"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import type { GlassProps as LensProps } from "@samasante/liquid-glass";

import { cn } from "@/app/_lib/utils";

/**
 * Dark frosted-glass system built on @samasante/liquid-glass.
 *
 * The visible pill = a refraction lens (of the live page behind it) with a
 * chrome overlay layered on top: a shiny top specular edge + a 1px light rim +
 * a soft drop shadow — the Figma dark frosted pill. Optics are tuned LOW (subtle
 * bend + frost), meant to be read over the near-black topo CTA, not a busy canvas.
 *
 * SSR-guarded: the lens is a dynamic `ssr:false` chunk; before it mounts (and if
 * it throws) we render an identical pill with a plain frosted CSS fallback, so
 * the button always looks right with or without the lens.
 *
 * Green CTAs stay SOLID — use this only for the ✕ close, select chrome, and the
 * near-black CTA blocks.
 */

// Low, subtle optics — a gentle bend + frosted body with a slight dark veil.
export const GLASS_OPTICS_LOW: LensProps["optics"] = {
  strength: 0.12,
  dispersion: 0.12,
  frost: 8,
  specular: 0.4,
  glow: 0.15,
  bend: 0.2,
  brightness: -0.06,
  saturate: 1.1,
};

const GlassLens = dynamic(() => import("./glass-lens"), { ssr: false });

function useMounted() {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  return mounted;
}

class LensBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { err: boolean }
> {
  state = { err: false };
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    return this.state.err ? this.props.fallback : this.props.children;
  }
}

/**
 * The dark frosted-glass chrome: a light frosted veil, a base rim, the shiny
 * corner rim (bright at top-left + bottom-right — the liquid-glass "shiny
 * corners"), and a top specular sheen. Pure CSS so it reads correctly with or
 * without the refraction lens.
 */
function GlassChrome() {
  return (
    <>
      {/* light frosted veil so the pill reads a touch lighter than the black CTA */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit] bg-white/[0.07]"
      />
      {/* base 1px rim */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-white/15"
      />
      {/* shiny corner rim — bright at top-left & bottom-right corners */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          padding: 1,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.75) 100%)",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      {/* top specular sheen */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-[inherit] bg-gradient-to-b from-white/25 to-transparent"
      />
    </>
  );
}

/**
 * A refractive-glass surface: renders `children` crisp on top of the lens (or a
 * frosted fallback) with the chrome overlay. `radius` should match the visual
 * corner radius (px) so the lens + rounding line up. Handles its own SSR guard.
 */
export function Glass({
  className,
  children,
  radius = 24,
  optics,
  frosted = false,
}: {
  className?: string;
  children?: React.ReactNode;
  radius?: number;
  optics?: LensProps["optics"];
  /** Force the CSS-frosted look (skip the lens). */
  frosted?: boolean;
}) {
  const mounted = useMounted();
  const style = { borderRadius: radius } as React.CSSProperties;

  const body = (withFrost: boolean) => (
    <span
      className={cn(
        "relative isolate flex items-center justify-center overflow-hidden",
        withFrost && "bg-white/[0.06] backdrop-blur-md",
        className,
      )}
      style={style}
    >
      <GlassChrome />
      <span className="relative">{children}</span>
    </span>
  );

  if (frosted || !mounted) return body(true);

  return (
    <LensBoundary fallback={body(true)}>
      <GlassLens radius={radius} optics={{ ...GLASS_OPTICS_LOW, ...optics }}>
        {body(false)}
      </GlassLens>
    </LensBoundary>
  );
}

export interface GlassButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  /** Corner radius in px (default: full pill). */
  radius?: number;
}

/**
 * Dark frosted-glass button for near-black CTA blocks. Solid green CTAs elsewhere
 * should use the shadcn `Button`, not this.
 */
export const GlassButton = React.forwardRef<
  HTMLButtonElement,
  GlassButtonProps
>(({ className, children, radius = 999, disabled, ...props }, ref) => {
  const mounted = useMounted();
  const style = { borderRadius: radius } as React.CSSProperties;

  const pill = (withFrost: boolean) => (
    <span
      className={cn(
        "relative isolate flex items-center justify-center gap-2 overflow-hidden px-7 py-3.5 text-sm font-semibold text-white",
        withFrost && "bg-white/[0.06] backdrop-blur-md",
      )}
      style={style}
    >
      <GlassChrome />
      <span className="relative flex items-center gap-2">{children}</span>
    </span>
  );

  return (
    <button
      ref={ref}
      disabled={disabled}
      className={cn(
        "group relative inline-flex shadow-[0_12px_34px_-10px_rgba(0,0,0,0.65)] transition-transform duration-300 hover:-translate-y-0.5 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      style={style}
      {...props}
    >
      {mounted ? (
        <LensBoundary fallback={pill(true)}>
          <GlassLens radius={radius} optics={GLASS_OPTICS_LOW}>
            {pill(false)}
          </GlassLens>
        </LensBoundary>
      ) : (
        pill(true)
      )}
    </button>
  );
});
GlassButton.displayName = "GlassButton";
